import moment from 'moment';
import {
  CustomerCreditApplication,
  CustomerDetails,
  CustomerStatus,
  FINANCING_TYPE,
} from '@trnsact/trnsact-shared-types';
import { CreditAppTableRow, CustomerTableRow, DocumentsTableRow } from '../types';
import { Typography } from '@mui/material';
import { DateFormats } from 'constants/common';
import { formatCurrency } from 'utils';
import { CustomerStatusChip } from '../ui/components';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import DoneAllIcon from '@mui/icons-material/DoneAll';
import ViewListIcon from '@mui/icons-material/ViewList';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { capitalizeFirstLetter } from 'formatters';

export const formatCustomersToTableData = (customers: any[]): CustomerTableRow[] => {
  return customers.map(customer => ({
    id: customer.id,
    accountId: customer.accountId,
    name: customer.name ?? '',
    contactName: customer.contactName ?? '',
    phone: customer.phone ?? '',
    type: customer.type ?? '',
    lastContactDate: customer.lastContactDate ?? '',
    status: customer.status ?? CustomerStatus.Active,
    totalRevenueAmount: customer.totalRevenueAmount,
    activeDealsNumber: customer.activeDealsNumber,
    opportunitiesNumber: customer.opportunitiesNumber,
  }));
};

export const formatCreditAppsToTableData = (creditAppData: CustomerCreditApplication[]): CreditAppTableRow[] => {
  return creditAppData.map(ca => ({
    id: ca.creditApplicationId,
    vendorOpportunityId: ca.vendorOpportunity?.vendorOpportunityId ?? '',
    createdDateTime: ca.createdDateTime ?? '-',
    applyingFor: ca.applyingFor?.map(i => FINANCING_TYPE[i as keyof typeof FINANCING_TYPE] || i).join(', ') ?? '-',
    signedBy:
      ca.owners
        ?.filter(o => o!.signatureDate)
        .map(o => [o!.firstName, o!.lastName].filter(Boolean).join(' '))
        .join(', ') ?? '-',
    salesRep: ca.vendorOpportunity?.salesRepresentative?.fullName ?? '-',
    location: ca.location?.locationName ? `${ca.location.locationName}, ${ca.location.category}` : '-',
    equipment: ca.vendorOpportunity?.equipmentDescription ?? '-',
  }));
};

export const formatDocumentsToTableData = (customerDocuments: any[]): DocumentsTableRow[] => {
  const creditAppRows = customerDocuments.reduce<DocumentsTableRow[]>((acc, d) => {
    if (acc.find(r => r.vendorOpportunityId === d.document.vendorOpportunityId)) {
      return acc;
    }
    return [
      ...acc,
      {
        id: `${d.document.vendorOpportunityId}-credit-app`,
        documentId: '',
        vendorOpportunityId: d.document.vendorOpportunityId,
        source: '',
        docType: 'Credit Application',
        docDescription: '',
        createdBy: 'Trnsact',
        docName: 'Credit Application',
        createdDateTime: '',
        taskId: '',
        taskRegrading: '',
        customerAccountName: d.customerAccountName,
      },
    ];
  }, []);

  const customerDocumentsRows = customerDocuments.map(d => ({
    id: d.document.documentId,
    documentId: d.document.documentId,
    vendorOpportunityId: d.document.vendorOpportunityId,
    source: d.document.source,
    docType: capitalizeFirstLetter(d.document.docType ?? ''),
    docDescription: d.document.docDescription,
    createdBy: d.document.createdBy,
    docName: d.document.docName,
    createdDateTime: d.document.createdDateTime,
    taskId: d.task?.taskId ?? '',
    taskRegrading: d.task?.regarding ?? '',
    customerAccountName: d.customerAccountName,
  }));

  return [...creditAppRows, ...customerDocumentsRows];
};

export const getCustomerInfoCardsConfiguration = (customerDetails: CustomerDetails) => [
  {
    title: 'Customer Status',
    icon: <PersonIcon color="primary" />,
    content: <CustomerStatusChip status={customerDetails.status} />,
  },
  {
    title: 'First Contact',
    icon: <CalendarTodayIcon color="primary" />,
    content: (
      <Typography variant="h6" component="p">
        {customerDetails.firstContactDate ? moment(customerDetails.firstContactDate).format(DateFormats.Short) : '-'}
      </Typography>
    ),
  },
  {
    title: 'Last Contact',
    icon: <CalendarTodayIcon color="primary" />,
    content: (
      <Typography variant="h6" component="p">
        {customerDetails.lastContactDate ? moment(customerDetails.lastContactDate).format(DateFormats.Short) : '-'}
      </Typography>
    ),
  },
  {
    title: 'Last Sale',
    icon: <DoneAllIcon color="primary" />,
    content: (
      <Typography variant="h6" component="p">
        {customerDetails.lastSaleDate ? moment(customerDetails.lastSaleDate).format(DateFormats.Short) : '-'}
      </Typography>
    ),
  },
  {
    title: 'Open Opportunities',
    icon: <ViewListIcon color="primary" />,
    content: (
      <Typography variant="h6" component="p">
        {customerDetails.activeOpportunitiesNumber}
      </Typography>
    ),
  },
  {
    title: 'Total Revenue',
    icon: <AttachMoneyIcon color="primary" />,
    content: (
      <Typography variant="h6" component="p">
        {customerDetails.totalRevenueAmount ? formatCurrency(customerDetails.totalRevenueAmount) : '-'}
      </Typography>
    ),
  },
];

export const filterTableBySerachTerm = (data: any[], searchTerm: string, keysToFilter: string[]) =>
  data.filter(i => keysToFilter.some(k => (i[k] as string).toLowerCase().includes(searchTerm.toLowerCase())));
